/* eslint-disable react-refresh/only-export-components */
import { moneytransferApi, RelationshipEnum, shoppingApi, TransferStatusValueEnum } from 'app/src/services/welfareportal'
import { CheckCatalogButton, ErrorCancelPage, Image, PageWidget, PortalConfirmDialog } from 'app/src/shared/components'
import { CancelSuccess } from 'app/src/shared/components/CancelSuccess'
import {
  calculateAge,
  calculateAgeAccurate,
  euroFormatterIt,
  extractBirthDate,
  formatDateDDMMYYYY,
  formatDateYYYYMMDD,
} from 'app/src/shared/helpers'
import { useTranslation, useWebAppContext, useWelfarePortalNavigate } from 'app/src/shared/hooks'
import errorBlock from 'app/static/images/error_block.png'
import successImage from 'app/static/images/success_img.png'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { ProgressBar } from 'primereact/progressbar'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useCallback, useEffect, useMemo, useState } from 'react'

export enum TransferFondoSaluteWizardStepEnum {
  REQUEST_FORBIDDEN = -1,
  FIRST_STEP_WITHOUT_FAMILY_MEMBERS = 0,
  FIRST_STEP_WITH_FAMILY_MEMBERS = 1,
  SECOND_STEP_WITH_FAMILY_MEMBERS = 2,
  FINAL_STEP_SUBMITTED = 3,
  // FINAL_STEP_CONFIRMED = 4,
  ERROR_CONFIRM_TRANSFER_REJECTED = 4,
  ERROR_GENERIC = 5,
  DETAIL_STEP = 6,
}

export type FormDataType = {
  welfareCreditNumber?: number
  importToTransferNumber?: number
  importToTransfer?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldValue?: any
}

export function TransferFondoSaluteWizard(props: {
  transferDetails?: moneytransferApi.TransferResponseWithUserInfo
  creditDetails?: shoppingApi.CreditGetResult
  defaultValueImportInserito?: number
  initialStep: TransferFondoSaluteWizardStepEnum
}) {
  const context = useWebAppContext()
  const navigate = useWelfarePortalNavigate()
  const { t } = useTranslation('shopping', { keyPrefix: 'Transfer.FondoSalute' })
  const { t: tr } = useTranslation('shopping', { keyPrefix: 'Transfer' })
  const relationshipsT = useTranslation('userInfo', { keyPrefix: 'UserInfo.FamilyRelationships' })
  const [wizardStep, setWizardStep] = useState<TransferFondoSaluteWizardStepEnum>(props.initialStep)
  const [showModalConfirmSecondStepWithFamilyMembers, setShowModalConfirmSecondStepWithFamilyMembers] = useState<boolean>(false)
  const [showModalConfirmFirstStepWithoutFamilyMembers, setShowModalConfirmFirstStepWithoutFamilyMembers] =
    useState<boolean>(false)
  const [familyMembersWithEditedInfo, setFamilyMembersWithEditedInfo] = useState<Array<moneytransferApi.Relative> | undefined>([])
  const [btnContinuaDisabled, setBtnContinuaDisabled] = useState<boolean>(false)
  const [showModalCancel, setShowModalCancel] = useState<boolean>(false)
  const [transferConfirmResponse, setTransferConfirmResponse] = useState<moneytransferApi.TransferResponse>()
  const [showModal, setShowModal] = useState(false)
  const deleteOrder = shoppingApi.useOrderCancel()
  const createFamilyMemberMutation = moneytransferApi.useTransferConfirm({
    onError: (err) => {
      context.logger?.error('Error Creating Family Member', { err })
      //if (err.status === 400) setGeneralError('BAD REQUEST')
      //else setGeneralError('GENERIC ERROR')
    },
  })
  const [errorComponentVisible, setErrorComponentVisible] = useState(false)
  const [successComponentVisible, setSuccessComponentVisible] = useState(false)

  const handleOrderCancellation = async () => {
    try {
      await deleteOrder.mutateAsync({
        pathParams: { orderId: props.transferDetails?.orderId ?? '' },
      })
      setShowModal(false)
      setSuccessComponentVisible(true)
    } catch (error) {
      setErrorComponentVisible(true)
      console.error('Failed to cancel order:', error)
    }
  }

  const confirmTransfer = useCallback(
    async (requestBody: moneytransferApi.TransferConfirm) => {
      const mutateResp = await createFamilyMemberMutation.mutateAsync({
        pathParams: {
          transferId: props.transferDetails?.id ?? '',
        },
        body: {
          amount: requestBody.amount,
          familyMembers: requestBody.familyMembers,
        },
      })

      return mutateResp
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createFamilyMemberMutation],
  )

  const modalCancel = useMemo(() => {
    return (
      <div className='col-span-12'>
        <PortalConfirmDialog
          accept={async () => navigate.goToCatalog()}
          reject={() => setShowModalCancel(false)}
          yesText={t('WizardFirstStepWithoutFamilyMembers.modalCancelBtnConfirmLabel')}
          cancelText={t('WizardFirstStepWithoutFamilyMembers.modalCancelBtnCancelLabel')}
          title={t('WizardFirstStepWithoutFamilyMembers.modalCancelTitle')}
          body={null}
        />
      </div>
    )
  }, [navigate, t])

  useEffect(() => {
    let fm: Array<moneytransferApi.Relative> | undefined = []
    fm = props.transferDetails?.familyMembers?.map((f) => {
      const familyMember: moneytransferApi.Relative = {
        firstName: f.firstName,
        fiscalCode: f.fiscalCode,
        lastName: f.lastName,
        relationship: f.relationship,
        birthDate: extractBirthDate(f.fiscalCode), //se non vengo dalla schermata successiva (Indietro) inizializzo la DataNascita ricavandola dal CF
        taxDependent: f.taxDependent,
      }
      return familyMember
    })
    setFamilyMembersWithEditedInfo(fm)
    refreshDisablingBtnContinua(fm)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function refreshDisablingBtnContinua(fm?: Array<moneytransferApi.Relative>) {
    const fmWithWarning: Array<moneytransferApi.Relative> | undefined = fm?.filter((f) => {
      if (checkWarningEtaFutura(f) || checkWarningBeneficiario(f)) return f
    })

    if (fmWithWarning && fmWithWarning.length > 0) setBtnContinuaDisabled(true)
    else setBtnContinuaDisabled(false)
  }

  function renderFirstStepWithoutFamilyMembers() {
    return (
      <PageWidget>
        <div className='grid columns-12'>
          <div className='col-span-12 mt-5 text-left text-2xl font-semibold leading-9'>
            <p>{t('WizardFirstStepWithoutFamilyMembers.title')}</p>
          </div>
          <div className='col-span-12 md:col-span-4 md:col-start-1'>
            <div className='mt-6 text-left text-lg font-normal leading-7'>
              {t('WizardFirstStepWithoutFamilyMembers.labelProduct')}
            </div>
            <div className='mt-5 text-left text-2xl font-semibold leading-7'>{props.transferDetails?.productName}</div>
          </div>
          <div className='col-span-12 md:col-span-5 md:col-start-7'>
            <div className='mt-6 text-left text-lg font-normal leading-7'>
              {t('WizardFirstStepWithoutFamilyMembers.labelImport')}
            </div>
            <div className='mt-5 text-left text-[26px] font-bold leading-9'>
              <b>{props.transferDetails?.amount ? euroFormatterIt.format(props.transferDetails.amount!) : ''}</b>
            </div>
            <div className='mt-4 text-sm font-normal'>
              {t('WizardFirstStepWithoutFamilyMembers.creditAvailableLabel')}
              {props.creditDetails?.creditByServiceCategory['welfareArt100']?.available && (
                <b>{euroFormatterIt.format(props.creditDetails?.creditByServiceCategory['welfareArt100']?.available)}</b>
              )}
            </div>
          </div>
          <div className='col-span-12 mt-9'>
            <hr className='border-t-solid border-t-[1px] border-[#0047BB]'></hr>
          </div>
          <div className='col-span-12 mt-9'>
            <div className='text-lg font-bold leading-7'>{t('WizardFirstStepWithoutFamilyMembers.detailTitle')}</div>
            <div className='mt-6 text-sm font-normal leading-4'>
              {t('WizardFirstStepWithoutFamilyMembers.detailBenecifiaryLabel')}
            </div>
            <div className='mt-4 text-[16px] font-normal leading-5'>
              {props.transferDetails?.userInfo.firstName} {props.transferDetails?.userInfo.lastName}
            </div>
          </div>
          <div className='col-span-12 mt-12 flex flex-col items-center'>
            <Button
              rounded
              className='h-[38px] min-w-[150px] text-[14px] font-normal'
              label={t('WizardFirstStepWithoutFamilyMembers.btnConfirmLabel')}
              onClick={() => setShowModalConfirmFirstStepWithoutFamilyMembers(true)}
            />
            <Button
              label={t('WizardFirstStepWithoutFamilyMembers.btnCancelLabel')}
              text
              className='mt-[32px] p-0 text-left'
              pt={{
                label: { className: 'text-[18px] text-[#323130] underline font-normal leading-[22px]' },
              }}
              onClick={() => setShowModalCancel(true)}
            />
          </div>
          {showModalConfirmFirstStepWithoutFamilyMembers && (
            <div className='col-span-12'>
              <PortalConfirmDialog
                accept={async () => onClickConfirmTransfer(false)}
                reject={() => setShowModalConfirmFirstStepWithoutFamilyMembers(false)}
                yesText={t('WizardFirstStepWithoutFamilyMembers.modalConfirmBtnConfirmLabel')}
                cancelText={t('WizardFirstStepWithoutFamilyMembers.modalConfirmBtnCancelLabel')}
                title={t('WizardFirstStepWithoutFamilyMembers.modalConfirmTitle')}
                body={null}
              />
            </div>
          )}
          {showModalCancel && modalCancel}
        </div>
      </PageWidget>
    )
  }

  function onChangeBirthDate(date: Date, familyMemberIndex: number) {
    let fm: Array<moneytransferApi.Relative> | undefined = []

    fm = familyMembersWithEditedInfo?.map((v, i) => {
      if (i == familyMemberIndex) {
        v.birthDate = formatDateYYYYMMDD(date as Date)
        return v
      } else return v
    })

    setFamilyMembersWithEditedInfo(fm)

    refreshDisablingBtnContinua(fm)
  }

  function checkWarningBeneficiario(familyMember: moneytransferApi.RelativeResponse) {
    if (familyMember.birthDate) {
      if (
        familyMember.relationship == RelationshipEnum.FiglioFiglia.toString() &&
        calculateAge(new Date(familyMember.birthDate)) > 26
      )
        return true
      else return false
    } else return false
  }

  function checkWarningEtaFutura(familyMember: moneytransferApi.RelativeResponse) {
    if (familyMember.birthDate) {
      if (calculateAgeAccurate(new Date(familyMember.birthDate)) < 0) return true
      else return false
    } else return false
  }

  function renderWarningEtaBeneficiario(familyMember: moneytransferApi.RelativeResponse) {
    if (checkWarningEtaFutura(familyMember))
      return (
        <div className='grid grid-cols-12'>
          <div className='col-span-4 mt-[10px] bg-[#F1D8DA] p-[15px] text-[13px] font-normal leading-[17px] text-[#955458]'>
            {t('warnBeneficiaryWrongAge')}
          </div>
        </div>
      )
    else {
      if (checkWarningBeneficiario(familyMember))
        return (
          <div className='grid grid-cols-12'>
            <div className='col-span-4 mt-[10px] bg-[#F1D8DA] p-[15px] text-[13px] font-normal leading-[17px] text-[#955458]'>
              {t('warnBeneficiaryMaxAge')}
            </div>
          </div>
        )
      else return
    }
  }

  function renderRowFamiliare(
    familyMember: moneytransferApi.RelativeResponse,
    i: number,
    readOnly: boolean,
    withBackground?: boolean,
  ) {
    return (
      <div>
        <div key={`familiar-${i}`} className={withBackground ? `col-span-12 bg-[#F2F8FF] p-5` : `col-span-12 mt-[45px]`}>
          <div className='col-span-12'>
            <div className='text-[18px] font-bold leading-[22px]'>
              {t('beneficiaryTitle')}
              {i + 1}
            </div>
            <div className='mt-[20px] text-[14px] font-normal leading-4'>{t('beneficiaryName')}</div>
            <div className='mt-[15px] text-[14px] font-normal leading-5'>
              <b>
                {familyMember.firstName} {familyMember.lastName}
              </b>{' '}
              - {familyMember.fiscalCode} - {relationshipsT.t(familyMember.relationship)?.label}
            </div>
          </div>
          {readOnly ? (
            <div className='col-span-12 mt-[25px]'>
              <label htmlFor={`dataNascita-${i}`} className='my-2 block text-[14px] font-normal'>
                {t('beneficiaryBirthDate')}
              </label>
              <div id={`dataNascita-${i}`} className='mt-[20px] text-[14px] font-normal leading-[17px]'>
                {familyMember?.birthDate ? formatDateDDMMYYYY(new Date(familyMember.birthDate!), true) : ''}
              </div>
            </div>
          ) : (
            <div className='col-span-12 mt-[25px]'>
              <label htmlFor={`dataNascita-${i}`} className='my-2 block text-[14px] font-normal'>
                {t('beneficiaryBirthDate')}
              </label>
              <Calendar
                className='max-w-[300px]'
                id={`dataNascita-${i}`}
                value={
                  familyMember.birthDate ? new Date(familyMember.birthDate) : new Date(extractBirthDate(familyMember.fiscalCode))
                }
                showIcon
                readOnlyInput={false}
                // showButtonBar
                placeholder='GG/MM/AAAA'
                dateFormat='dd/mm/yy'
                onChange={(ev) => (ev.value ? onChangeBirthDate(ev.value! as Date, i) : null)}
              />
              {renderWarningEtaBeneficiario(familyMember)}
            </div>
          )}
        </div>
        {withBackground && <div className='mt-5' />}
      </div>
    )
  }

  function renderRowsFamiliari(
    familyMembers: moneytransferApi.Relative[] | moneytransferApi.RelativeResponse[],
    readOnly: boolean,
    withBackground?: boolean,
  ) {
    const rows: JSX.Element[] = []

    familyMembers?.forEach((f, i) => {
      rows.push(renderRowFamiliare(f, i, readOnly, withBackground))
    })

    return rows
  }

  function renderFirstStepWithFamilyMembers() {
    return (
      <PageWidget>
        <div className='grid columns-12'>
          <Button link onClick={() => setShowModalCancel(true)} icon='pi pi-arrow-left' pt={{ root: { className: 'mb-2 pl-0' } }}>
            <span className='ml-2 font-bold'>{tr('abortRequestBtnLabel')}</span>
          </Button>
          <div className='col-span-12 text-[18px] font-semibold leading-5'>{t('WizardFirstStepWithFamilyMembers.labelStep')}</div>
          <div className='col-span-12 mt-2'>
            <ProgressBar className='h-[10px] rounded-none' showValue={false} value={50}></ProgressBar>
          </div>
          <div className='col-span-12 mt-11 text-left text-2xl font-semibold leading-9'>
            <p>{t('WizardFirstStepWithFamilyMembers.title')}</p>
          </div>
          <div className='col-span-12 mt-10 text-left text-base font-normal leading-9'>
            <p>{t('WizardFirstStepWithFamilyMembers.subtitle')}</p>
          </div>
        </div>
        <div className='col-span-12'>{renderRowsFamiliari(familyMembersWithEditedInfo!, false)}</div>
        <div className='mt-10 flex flex-col items-center'>
          <Button
            rounded
            className='h-[38px] min-w-[150px] text-[14px] font-normal'
            label={t('WizardFirstStepWithFamilyMembers.btnContinueLabel')}
            disabled={btnContinuaDisabled}
            onClick={() => setWizardStep(TransferFondoSaluteWizardStepEnum.SECOND_STEP_WITH_FAMILY_MEMBERS)}
          />
        </div>
        {showModalCancel && modalCancel}
      </PageWidget>
    )
  }

  const onClickConfirmTransfer = async (withFamilyMembers: boolean) => {
    try {
      const body: moneytransferApi.TransferConfirm = {
        // amount: props.transferDetails.amount,
        familyMembers: withFamilyMembers ? familyMembersWithEditedInfo : [],
      }
      const transferConfirmResp: moneytransferApi.TransferResponse = await confirmTransfer(body)
      setTransferConfirmResponse(transferConfirmResp)
      if (
        transferConfirmResp.status.value == TransferStatusValueEnum.SUBMITTED.toString() ||
        transferConfirmResp.status.value == TransferStatusValueEnum.CANCELED.toString()
      )
        setWizardStep(TransferFondoSaluteWizardStepEnum.FINAL_STEP_SUBMITTED)
      else if (transferConfirmResp.status.value == TransferStatusValueEnum.REJECTED.toString())
        setWizardStep(TransferFondoSaluteWizardStepEnum.ERROR_CONFIRM_TRANSFER_REJECTED)
    } catch (err) {
      context.logger?.error('Error Confirm Transfer', { err })
      setWizardStep(TransferFondoSaluteWizardStepEnum.ERROR_GENERIC)
    }
  }

  function renderSecondStepWithFamilyMembers() {
    return (
      <PageWidget>
        <div className='grid columns-12'>
          <Button
            link
            onClick={() => setWizardStep(TransferFondoSaluteWizardStepEnum.FIRST_STEP_WITH_FAMILY_MEMBERS)}
            icon='pi pi-arrow-left'
            pt={{ root: { className: 'mb-2 pl-0' } }}
          >
            <span className='ml-2 font-bold'>{tr('goBackBtnLabel')}</span>
          </Button>
          <div className='col-span-12 text-[18px] font-semibold leading-5'>
            {t('WizardSecondStepWithFamilyMembers.labelStep')}
          </div>
          <div className='col-span-12 mt-2'>
            <ProgressBar className='h-[10px] rounded-none' showValue={false} value={100}></ProgressBar>
          </div>
          <div className='col-span-12 mt-5 text-left text-2xl font-semibold leading-9'>
            <p>{t('WizardSecondStepWithFamilyMembers.title')}</p>
          </div>
          <div className='col-span-12 md:col-span-4 md:col-start-1'>
            <div className='mt-6 text-left text-lg font-normal leading-7'>
              {t('WizardSecondStepWithFamilyMembers.labelProduct')}
            </div>
            <div className='mt-5 text-left text-2xl font-semibold leading-7'>{props.transferDetails?.productName}</div>
          </div>
          <div className='col-span-12 md:col-span-6 md:col-start-6'>
            <div className='mt-6 text-left text-lg font-normal leading-7'>
              {t('WizardSecondStepWithFamilyMembers.labelImport')}
            </div>
            <div className='mt-5 text-left text-[26px] font-bold leading-9'>
              <b>{props.transferDetails?.amount ? euroFormatterIt.format(props.transferDetails.amount!) : ''}</b>
            </div>
            <div className='mt-4 text-sm font-normal'>
              {t('WizardSecondStepWithFamilyMembers.creditAvailableLabel')}
              {props.creditDetails?.creditByServiceCategory['welfareArt100']?.available && (
                <b>{euroFormatterIt.format(props.creditDetails?.creditByServiceCategory['welfareArt100']?.available)}</b>
              )}
            </div>
          </div>
          <div className='col-span-12 mt-9'>
            <hr className='border-t-solid border-t-[1px] border-[#0047BB]'></hr>
          </div>
          <div className='col-span-12 mt-9'>
            <div className='text-lg font-bold leading-7'>{t('WizardSecondStepWithFamilyMembers.detailTitle')}</div>
            <div className='mt-6 text-sm font-normal leading-4'>
              {t('WizardSecondStepWithFamilyMembers.detailBenecifiaryLabel')}
            </div>
            <div className='mt-4 text-[16px] font-normal leading-5'>
              {props.transferDetails?.userInfo.firstName} {props.transferDetails?.userInfo.lastName}
            </div>
          </div>
          <div className='col-span-12'>{renderRowsFamiliari(familyMembersWithEditedInfo!, true)}</div>
          <div className='col-span-12 mt-12 flex flex-col items-center'>
            <Button
              rounded
              className='h-[38px] min-w-[150px] text-[14px] font-normal'
              label={t('WizardSecondStepWithFamilyMembers.btnConfirmLabel')}
              onClick={() => setShowModalConfirmSecondStepWithFamilyMembers(true)}
            />
            <Button
              label={t('WizardSecondStepWithFamilyMembers.btnCancelLabel')}
              text
              className='mt-[32px] p-0 text-left'
              pt={{
                label: { className: 'text-[18px] text-[#323130] underline font-normal leading-[22px]' },
              }}
              onClick={() => setShowModalCancel(true)}
            />
          </div>
          {showModalConfirmSecondStepWithFamilyMembers && (
            <div className='col-span-12'>
              <PortalConfirmDialog
                accept={async () => onClickConfirmTransfer(true)}
                reject={() => setShowModalConfirmSecondStepWithFamilyMembers(false)}
                yesText={t('WizardSecondStepWithFamilyMembers.modalConfirmBtnConfirmLabel')}
                cancelText={t('WizardSecondStepWithFamilyMembers.modalConfirmBtnCancelLabel')}
                title={t('WizardSecondStepWithFamilyMembers.modalConfirmTitle')}
                body={null}
              />
            </div>
          )}
          {showModalCancel && (
            <div className='col-span-12'>
              <PortalConfirmDialog
                accept={async () => navigate.goToCatalog()}
                reject={() => setShowModalCancel(false)}
                yesText={t('WizardSecondStepWithFamilyMembers.modalCancelBtnConfirmLabel')}
                cancelText={t('WizardSecondStepWithFamilyMembers.modalCancelBtnCancelLabel')}
                title={t('WizardSecondStepWithFamilyMembers.modalCancelTitle')}
                body={null}
              />
            </div>
          )}
        </div>
      </PageWidget>
    )
  }

  function renderFinalStepSubmitted() {
    return (
      <PageWidget>
        <div>
          <div>
            <Image
              pt={{ image: { className: 'object-fit mx-auto h-[150px] lg:h-[250px]' } }}
              src={successImage}
              alt='Immagine di successo'
            />
          </div>
          <div className='text-center text-[28px] font-bold leading-10'>
            <p>{t('WizardFinalStepSubmitted.title')}</p>
            <p>{t('WizardFinalStepSubmitted.subtitle')}</p>
          </div>
          <div className='mt-5 text-center text-[18px] font-normal leading-6'>
            <p>{t('WizardFinalStepSubmitted.description')}</p>
          </div>
          <div className='mx-auto mt-8 flex justify-center'>
            <CheckCatalogButton />
          </div>
        </div>
      </PageWidget>
    )
  }

  function renderErrorGeneric() {
    return (
      <PageWidget>
        <div className='mb-2 flex flex-col'>
          <div className='mx-auto'>
            <div>
              <Image
                pt={{ image: { className: 'max-h-[257px]' } }}
                className='object-fit aspect-[3/2] max-h-[257px]'
                src={errorBlock}
              />
            </div>
          </div>
          <div className='bold m-auto mx-auto mt-[24px] max-w-xl text-center text-[22px] font-bold leading-[40px]'>
            {t('WizardErrorGeneric.title')}
          </div>
          <div className='mx-auto flex flex-col items-center justify-center'>
            {/* <Button
              rounded
              className='mt-10 h-[38px] min-w-[150px] text-[14px] font-normal'
              label={t('WizardErrorGeneric.btnRetryLabel')}
              //onClick={}
            /> */}
            <Button
              rounded
              label={t('WizardErrorGeneric.btnGoToCatalogLabel')}
              className='my-5 h-[38px] min-w-[150px] text-[14px] font-normal'
              onClick={navigate.goToCatalog}
            />
          </div>
        </div>
      </PageWidget>
    )
  }

  function renderErrorConfirmTransferRejected() {
    return (
      <PageWidget>
        <div className='mb-2 flex flex-col'>
          <div className='mx-auto'>
            <div>
              <Image
                pt={{ image: { className: 'max-h-[257px]' } }}
                className='object-fit aspect-[3/2] max-h-[257px]'
                src={errorBlock}
              />
            </div>
          </div>
          <div className='bold m-auto mx-auto mt-[24px] max-w-xl text-center text-[22px] font-bold leading-[40px]'>
            <p>{tr('errorConfirmTitle')}</p>
            <p>{transferConfirmResponse?.status?.message}</p>
          </div>
          <div className='mx-auto flex flex-col items-center justify-center'>
            {/* <Button
              rounded
              className='mt-10 h-[38px] min-w-[150px] text-[14px] font-normal'
              label={t('WizardErrorGeneric.btnRetryLabel')}
              //onClick={}
            /> */}
            <Button
              rounded
              label={t('WizardErrorGeneric.btnGoToCatalogLabel')}
              className='my-5 h-[38px] min-w-[150px] text-[14px] font-normal'
              onClick={navigate.goToCatalog}
            />
          </div>
        </div>
      </PageWidget>
    )
  }

  function renderStatus() {
    switch (props.transferDetails?.status.value) {
      case TransferStatusValueEnum.SUBMITTED.toString():
        return (
          <div className='mt-[25px] w-fit bg-[#EEDC00] p-2 text-sm font-bold leading-3 text-[#0047BB]'>
            {tr('StateMapping.SUBMITTED.label')}
          </div>
        )
        break
      case TransferStatusValueEnum.CONFIRMED.toString():
        return (
          <div className='mt-[25px] w-fit bg-[#EEDC00] p-2 text-sm font-bold leading-3 text-[#0047BB]'>
            {tr('StateMapping.CONFIRMED.label')}
          </div>
        )
        break
      case TransferStatusValueEnum.CANCELED.toString():
        return (
          <div className='mt-[25px] w-fit bg-[#fae8eb] p-2 text-sm font-bold leading-3 text-[#BF0B20]'>
            {tr('StateMapping.CANCELED.label')}
          </div>
        )
        break
      case TransferStatusValueEnum.REJECTED.toString():
        return (
          <div className='mt-[25px] w-fit bg-[#fae8eb] p-2 text-sm font-bold leading-3 text-[#BF0B20]'>
            {tr('StateMapping.REJECTED.label')}
          </div>
        )
        break

      default:
        return <div className='mt-[25px] w-fit bg-[#EEDC00] p-2 text-sm font-bold leading-3 text-[#0047BB]'>Non valido</div>
        break
    }
  }

  function renderErrorBox() {
    if (props.transferDetails?.status.value === TransferStatusValueEnum.REJECTED.toString()) {
      return (
        <div className='mt-8 grid grid-cols-12 bg-[#EAEAEA] p-5'>
          <div className='col-span-12 mt-2 text-sm font-bold leading-4'>{tr('Detail.errorTitle')}</div>
          <div className='col-span-12 mt-2 text-sm font-normal leading-4'>{tr('Detail.errorDescription')}</div>
          <div className='col-span-12 mt-2 text-sm font-normal leading-4'>{props.transferDetails?.status?.message}</div>
        </div>
      )
    } else return
  }

  function renderDetail() {
    return (
      <PageWidget>
        <div>
          <div className='text-center'>
            <p className='text-2xl font-semibold leading-9'>{tr('Detail.title')}</p>
          </div>
          {props.transferDetails?.isCancellable && props.transferDetails.status.value !== 'CANCELED' && (
            <div className='flex justify-end'>
              <Button link onClick={() => setShowModal(true)} icon='pi pi-trash' label={t('cancelOrder.cancelButtonLabel')} />
            </div>
          )}
          <div className='mt-9 grid grid-cols-12 md:grid-cols-3'>
            <div className='col-span-12 md:col-span-1'>
              {renderStatus()}
              <div className='mt-5 text-2xl font-semibold leading-7'>{props.transferDetails?.productName}</div>
            </div>
            <div className='col-span-12 flex flex-col items-center max-md:hidden md:col-span-1'>
              <hr className='h-[150px] w-[1px] border-[1px] border-solid border-[var(--primary-color)]'></hr>
            </div>
            <div className='col-span-12 flex flex-col items-center md:col-span-1 md:hidden'>
              <hr className='my-10 w-full rotate-0 border-[1px] border-solid border-[var(--primary-color)]'></hr>
            </div>
            <div className='col-span-12 md:col-span-1'>
              <div className='mt-1 text-left text-lg font-normal leading-7 md:mt-6'>{tr('Detail.labelImport')}</div>
              <div className='mt-5 text-left text-[26px] font-bold leading-9'>
                <b>{props.transferDetails?.amount ? euroFormatterIt.format(props.transferDetails.amount!) : ''}</b>
              </div>
            </div>
          </div>
          {renderErrorBox()}
          <div className='mt-9 grid grid-cols-12 bg-[#F2F8FF] p-5'>
            <div className='col-span-12'>
              <div className='w-full break-words text-sm font-normal leading-4'>
                {tr('Detail.orderIdLabel')}{' '}
                <span className='mt-4 text-[16px] font-bold leading-5'>{props.transferDetails?.id ?? '-'}</span>
              </div>
              <div className='my-4 h-px w-full bg-black'></div>
            </div>
            <div className='col-span-12 md:col-span-6'>
              <div className='mt-2 text-sm font-normal leading-4'>{tr('Detail.detailBenecifiaryLabel')}</div>
              <div className='mt-4 text-[16px] font-bold leading-5'>
                {props.transferDetails?.userInfo.firstName} {props.transferDetails?.userInfo.lastName}
              </div>
            </div>
            <div className='col-span-12 md:col-span-6 md:ml-2'>
              <div className='mt-2 text-sm font-normal leading-4'>{tr('Detail.detailDate')}</div>
              <div className='mt-4 text-[16px] font-bold leading-5'>
                {props.transferDetails?.date ? formatDateDDMMYYYY(new Date(props.transferDetails.date!), true) : ''}
              </div>
            </div>
          </div>
          <div className='mt-5' />
          {props.transferDetails?.familyMembers && (
            <div className='col-span-12'>{renderRowsFamiliari(props.transferDetails?.familyMembers, true, true)}</div>
          )}
        </div>
        <div className='mx-auto flex justify-center'>
          <CheckCatalogButton />
        </div>
        {showModal && (
          <PortalConfirmDialog
            body={t('cancelOrder.body')}
            title={t('cancelOrder.title')}
            reject={() => setShowModal(false)}
            accept={handleOrderCancellation}
          />
        )}
      </PageWidget>
    )
  }
  if (errorComponentVisible) {
    return (
      <ErrorCancelPage
        errorTitle={t('cancelOrder.cancelErrorMessage.title')}
        errorSubtitle={t('cancelOrder.cancelErrorMessage.subtitle')}
      >
        <div className='mx-auto flex justify-center'>
          <CheckCatalogButton />
        </div>
      </ErrorCancelPage>
    )
  }
  if (successComponentVisible) {
    return (
      <CancelSuccess
        successTitle={t('cancelOrder.cancelSuccessMessage.title')}
        successSubTitle={t('cancelOrder.cancelSuccessMessage.subtitle')}
      >
        <div className='mx-auto flex justify-center'>
          <CheckCatalogButton />
        </div>
      </CancelSuccess>
    )
  }
  if (deleteOrder.isPending)
    return (
      <div className='mt-2 flex'>
        <div className='mx-auto'>
          <ProgressSpinner
            data-testid='loading-element'
            pt={{
              root: {
                className: 'w-[50px] h-[50px]',
              },
            }}
          />
        </div>
      </div>
    )

  switch (wizardStep) {
    case TransferFondoSaluteWizardStepEnum.FIRST_STEP_WITH_FAMILY_MEMBERS:
      return <PageWidget>{renderFirstStepWithFamilyMembers()}</PageWidget>
      break
    case TransferFondoSaluteWizardStepEnum.FIRST_STEP_WITHOUT_FAMILY_MEMBERS:
      return <PageWidget>{renderFirstStepWithoutFamilyMembers()}</PageWidget>
      break
    case TransferFondoSaluteWizardStepEnum.SECOND_STEP_WITH_FAMILY_MEMBERS:
      return <PageWidget>{renderSecondStepWithFamilyMembers()}</PageWidget>
      break
    case TransferFondoSaluteWizardStepEnum.FINAL_STEP_SUBMITTED:
      return <PageWidget>{renderFinalStepSubmitted()}</PageWidget>
    case TransferFondoSaluteWizardStepEnum.ERROR_GENERIC:
      return <PageWidget>{renderErrorGeneric()}</PageWidget>
    case TransferFondoSaluteWizardStepEnum.ERROR_CONFIRM_TRANSFER_REJECTED:
      return <PageWidget>{renderErrorConfirmTransferRejected()}</PageWidget>
    case TransferFondoSaluteWizardStepEnum.DETAIL_STEP:
      return <PageWidget>{renderDetail()}</PageWidget>
    default:
      break
  }
}
